import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListCheck,
  faIndustry,
  faTriangleExclamation,
  faExplosion,
  faCubesStacked,
  faFileLines,
  faSun,
  faMountain,
  faScaleBalanced,
  faHammer,
  faArrowsLeftRight
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { RootState } from "../../state/store";
import { hasAnyRole } from "../../utilities/helpers/roleUtilities";
import { UserDto } from "../../DTOs/User/UserDto";

type TileResolver = (modules: Record<string, string>, user: UserDto | null) => string;

type QuickTile = {
  to: string | TileResolver;
  label: string;
  icon: IconDefinition;
  access: (modules: Record<string, string>) => string[];
  accentClass: string;
};

const TILES: QuickTile[] = [
  {
    to: "/operations",
    label: "Operations",
    icon: faListCheck,
    access: (m) => [m.mining, m.lotusHall],
    accentClass: "text-theme-info",
  },
  {
    to: "/production/show",
    label: "Production",
    icon: faIndustry,
    access: (m) => [m.mining, m.lotusHall],
    accentClass: "text-theme-danger",
  },
  {
    to: "/production/crushing",
    label: "Crushing",
    icon: faHammer,
    access: (m) => [m.mining, m.lotusHall, m.geology],
    accentClass: "text-theme-warning",
  },
  {
    to: "/production/rehandle",
    label: "Rehandle",
    icon: faArrowsLeftRight,
    access: (m) => [m.mining, m.lotusHall, m.geology],
    accentClass: "text-theme-info",
  },
  {
    to: "/stoppages",
    label: "Stoppages",
    icon: faTriangleExclamation,
    access: (m) => [m.mining, m.lotusHall, m.maintenance],
    accentClass: "text-theme-warning",
  },
  {
    to: "/db/bh-drilling",
    label: "Drill & Blast",
    icon: faExplosion,
    access: (m) => [m.mining, m.lotusHall],
    accentClass: "text-theme-danger",
  },
  {
    to: "/geology/stockpile-balances",
    label: "Stockpiles",
    icon: faCubesStacked,
    access: (m) => [m.geology],
    accentClass: "text-theme-success",
  },
  {
    to: "/geology/production",
    label: "Geology Mvt",
    icon: faMountain,
    access: (m) => [m.geology],
    accentClass: "text-theme-success",
  },
  {
    to: (m, user) =>
      hasAnyRole(user, [m.geology]) ? "/geology/ore-tally" : "/production/ore-tally",
    label: "Ore Tally",
    icon: faScaleBalanced,
    access: (m) => [m.mining, m.lotusHall, m.geology],
    accentClass: "text-theme-info-hover",
  },
  {
    to: "/reports/daily",
    label: "Daily Report",
    icon: faFileLines,
    access: (m) => [m.mining],
    accentClass: "text-theme-info",
  },
  {
    to: "/reports/morning",
    label: "Morning Meeting",
    icon: faSun,
    access: (m) => [m.mining],
    accentClass: "text-theme-warning",
  },
];

export const DashboardQuickTiles: React.FC = () => {
  const { user, roleConfig } = useSelector((s: RootState) => s.authReducer);

  const visibleTiles = useMemo(() => {
    if (!roleConfig?.modules) return [] as QuickTile[];
    return TILES.filter((tile) => {
      const required = tile.access(roleConfig.modules).filter(Boolean);
      if (required.length === 0) return true;
      return hasAnyRole(user, required);
    });
  }, [user, roleConfig]);

  if (visibleTiles.length === 0) return null;

  return (
    <div className="w-full">
      <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-9">
        {visibleTiles.map((tile) => {
          const resolvedTo =
            typeof tile.to === "function"
              ? tile.to(roleConfig!.modules, user)
              : tile.to;
          return (
            <Link
              key={resolvedTo}
              to={resolvedTo}
              className="group flex flex-col items-center justify-center gap-2 px-3 py-4 rounded-lg transition
                        border border-theme-heading-border bg-theme-surface-2 shadow-md shadow-theme-shadow text-theme-text
                        hover:shadow-sm hover:shadow-theme-shadow hover:border-theme-focus hover:translate-y-[1px]
                        active:shadow-none active:translate-y-[2px]
"
            >
              <FontAwesomeIcon
                icon={tile.icon}
                className={`text-2xl md:text-3xl ${tile.accentClass} group-hover:scale-110 transition-transform`}
              />
              <span className="text-sm md:text-base font-medium text-center leading-tight">
                {tile.label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
